/* eslint-disable react/no-unused-prop-types */
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

type MaybeNumber = number | string | null

interface DmlCardDataItem {
    ATE?: MaybeNumber
    Effect_Size_Unit_Measurement?: MaybeNumber
    Lift?: MaybeNumber
    Total_Effect_From_Exp?: MaybeNumber
    P_Value_Primary?: MaybeNumber
    P_Value?: MaybeNumber
    P_Value_Secondary?: MaybeNumber
    ate_lower_bound?: MaybeNumber
    ate_upper_bound?: MaybeNumber
    avg_post_counterfactual?: MaybeNumber
    avg_post_for_treatment?: MaybeNumber
    avg_post_synthetic_control?: MaybeNumber
    Level_Status?: string
    Exp_Status?: string
    Remarks?: string
    observed_after_intervention?: MaybeNumber
    counterfactual_after_intervention?: MaybeNumber
    observedAfter?: MaybeNumber
    counterfactualAfter?: MaybeNumber
    test_value?: MaybeNumber
    expected_value?: MaybeNumber
    average_treatment_effect?: MaybeNumber
    ate?: MaybeNumber
    impact?: MaybeNumber
    total_kpi_impact?: MaybeNumber
    totalImpact?: MaybeNumber
    total_effect?: MaybeNumber
    percentage_lift?: MaybeNumber
    uplift?: MaybeNumber
    lift_pct?: MaybeNumber
    ci_lower_95?: MaybeNumber
    ci_upper_95?: MaybeNumber
    lower_bound?: MaybeNumber
    upper_bound?: MaybeNumber
    wald_p_value?: MaybeNumber
    placebo_p_value?: MaybeNumber
    p_value?: MaybeNumber
    kpi_unit?: string
    unit?: string
    summary?: string
}

interface Props {
    experimentDetails: any
    selectedGroup: string | null
    selectedKPI: any
    selectedDateRange: any[]
    selectedOptionConfig?: string | null
    toDownloadAsReport?: boolean
    reportError?: (x: boolean) => void
    dmlCardData?: DmlCardDataItem[] | DmlCardDataItem | null
}

const toNumber = (value?: MaybeNumber): number | null => {
    if (value === null || value === undefined || value === '') return null
    const numericValue = Number(value)
    return Number.isFinite(numericValue) ? numericValue : null
}

const pickNumber = (...values: (MaybeNumber | undefined)[]) => {
    for (const value of values) {
        const numericValue = toNumber(value)
        if (numericValue !== null) {
            return numericValue
        }
    }
    return null
}

const pickString = (...values: (string | null | undefined)[]) => {
    for (const value of values) {
        if (typeof value === 'string' && value.trim()) {
            return value.trim()
        }
    }
    return null
}

const formatPercent = (value: number | null, digits = 1) => {
    const formatted = formatSigned(value, digits)

    return formatted === '—' ? formatted : `${formatted}%`
}

const formatValueWithUnit = (value: string, unit?: string | null) => {
    if (value === '—' || !unit) return value

    return `${value} ${unit}`
}

const resolveSelectedKpiValue = (selectedKPI: any) => {
    if (typeof selectedKPI === 'string') {
        return selectedKPI
    }

    return selectedKPI?.value || selectedKPI?.label || null
}

const resolveSelectedKpiLabel = (selectedKPI: any) => {
    if (typeof selectedKPI === 'string') {
        return selectedKPI
    }

    return selectedKPI?.label || selectedKPI?.value || null
}

const unwrapDmlCardData = (cardData: Props['dmlCardData']): DmlCardDataItem | null => {
    if (!cardData) return null

    if (Array.isArray(cardData)) {
        return (cardData[0] ?? null) as DmlCardDataItem | null
    }

    if (Array.isArray((cardData as any)?.data)) {
        return ((cardData as any).data[0] ?? null) as DmlCardDataItem | null
    }

    if ((cardData as any)?.result && Array.isArray((cardData as any).result)) {
        return ((cardData as any).result[0] ?? null) as DmlCardDataItem | null
    }

    if ((cardData as any)?.dmlCardDetails) {
        return unwrapDmlCardData((cardData as any).dmlCardDetails)
    }

    return cardData as DmlCardDataItem
}

const formatSigned = (value: number | null, digits = 1) => {
    if (value === null || Number.isNaN(Number(value))) return '—'

    const normalized = Number(value)
    let prefix = ''
    if (normalized > 0) {
        prefix = '+'
    } else if (normalized < 0) {
        prefix = '−'
    }

    return `${prefix}${Math.abs(normalized).toLocaleString('en-US', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    })}`
}

const formatPlain = (value: number | null, digits = 1) => {
    if (value === null || Number.isNaN(Number(value))) return '—'

    return Number(value).toLocaleString('en-US', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    })
}

const formatCompactSigned = (value: number | null) => {
    if (value === null || Number.isNaN(Number(value))) return '—'

    const normalized = Number(value)
    let prefix = ''
    if (normalized > 0) {
        prefix = '+'
    } else if (normalized < 0) {
        prefix = '−'
    }
    const absolute = Math.abs(normalized)

    if (absolute >= 1000000) {
        return `${prefix}${formatPlain(absolute / 1000000, 2)}M`
    }

    if (absolute >= 1000) {
        return `${prefix}${formatPlain(absolute / 1000, 2)}K`
    }

    return `${prefix}${formatPlain(absolute, 1)}`
}

const resolveUnit = (experimentDetails: any, selectedKPI: any, payload: DmlCardDataItem) => {
    const selectedKpiValue = resolveSelectedKpiValue(selectedKPI)
    const metric = experimentDetails?.experiment_metadatas?.metrices?.find((item: any) => item?.literral_value === selectedKpiValue || item?.kpi_literal === selectedKpiValue)

    return payload?.kpi_unit?.trim() || payload?.unit?.trim() || metric?.kpi_unit?.trim() || ''
}

const DMLKpi: React.FC<Props> = ({ experimentDetails, selectedGroup, selectedKPI, selectedDateRange, selectedOptionConfig, toDownloadAsReport, reportError, dmlCardData = null }) => {
    const { t } = useTranslation()
    const selectedKpiValue = resolveSelectedKpiValue(selectedKPI)
    const selectedKpiLabel = resolveSelectedKpiLabel(selectedKPI)

    const payload = useMemo<DmlCardDataItem>(() => unwrapDmlCardData(dmlCardData) ?? {}, [dmlCardData])

    const unit = resolveUnit(experimentDetails, selectedKPI, payload)
    const kpiLabel = pickString(selectedKpiLabel, selectedKpiValue, experimentDetails?.experiment_metadatas?.metrices?.[0]?.literral_value) ?? '—'

    const observedAfter = pickNumber(payload.avg_post_for_treatment, payload.observed_after_intervention, payload.observedAfter, payload.test_value)
    const counterfactualAfter = pickNumber(payload.avg_post_counterfactual, payload.avg_post_synthetic_control, payload.counterfactual_after_intervention, payload.counterfactualAfter, payload.expected_value)
    const ate = pickNumber(payload.ATE, payload.Effect_Size_Unit_Measurement, payload.average_treatment_effect, payload.ate, payload.impact, payload.total_effect)
    const liftPct = pickNumber(payload.Lift, payload.percentage_lift, payload.uplift, payload.lift_pct)
    const lowerBound = pickNumber(payload.ate_lower_bound, payload.ci_lower_95, payload.lower_bound)
    const upperBound = pickNumber(payload.ate_upper_bound, payload.ci_upper_95, payload.upper_bound)
    const totalImpact = pickNumber(payload.Total_Effect_From_Exp, payload.total_kpi_impact, payload.totalImpact, payload.total_effect, payload.impact)

    const chartMax = Math.max(observedAfter ?? 0, counterfactualAfter ?? 0, 1)
    const chartScale = 206 / chartMax
    const observedBarHeight = observedAfter !== null ? Math.max(18, observedAfter * chartScale) : 0
    const counterfactualBarHeight = counterfactualAfter !== null ? Math.max(18, counterfactualAfter * chartScale) : 0

    const lowerRange = Math.min(0, lowerBound ?? 0, ate ?? 0)
    const upperRange = Math.max(0, upperBound ?? 0, ate ?? 0)
    const rangeSpan = Math.max(upperRange - lowerRange, 1)
    const toForestX = (value: number) => 210 + ((value - lowerRange) / rangeSpan) * 525

    const effectStatus = payload.Level_Status || payload.Exp_Status || null
    let effectHeading = t('Impact unavailable')

    if (effectStatus === 'Positive_Lift') {
        effectHeading = t('Positive impact with a statistically meaningful lift.')
    } else if (effectStatus === 'Negative_Lift') {
        effectHeading = t('Negative impact with a statistically meaningful lift.')
    } else if (liftPct !== null) {
        effectHeading = liftPct >= 0 ? t('Positive impact with a statistically meaningful lift.') : t('Negative impact with a statistically meaningful lift.')
    }

    let intervalInterpretation = t('is unavailable for the selected data.')

    if (lowerBound !== null) {
        intervalInterpretation = lowerBound > 0 ? t('remains above zero.') : t('crosses zero and should be interpreted with caution.')
    }

    const summaryCards = [
        {
            label: 'Percentage Lift',
            value: formatPercent(liftPct, 1),
            description: 'Estimated improvement versus the no-intervention counterfactual.',
        },
        {
            label: 'Average KPI Lift (ATE)',
            value: formatValueWithUnit(formatSigned(ate, 1), unit),
            description: 'Average treatment effect per treated record.',
        },
        {
            label: 'ATE Lower Bound',
            value: formatValueWithUnit(formatSigned(lowerBound, 1), unit),
            description: 'Value from ci_lower_95 for the average treatment effect.',
        },
        {
            label: 'ATE Upper Bound',
            value: formatValueWithUnit(formatSigned(upperBound, 1), unit),
            description: 'Value from ci_upper_95 for the average treatment effect.',
        },
        {
            label: 'Total KPI Impact',
            value: formatValueWithUnit(formatCompactSigned(totalImpact), unit),
            description: 'Estimated incremental volume across all treated records.',
            highlight: true,
        },
    ]

    return (
        <section className="space-y-4" data-testid="dml-kpi-section">
            <div
                className="hidden"
                aria-hidden="true"
                data-testid="dml-kpi-context"
                data-selected-range={selectedDateRange.length}
                data-selected-option={selectedOptionConfig ?? ''}
                data-download-report={toDownloadAsReport ? 'true' : 'false'}
                data-has-report-error={reportError ? 'true' : 'false'}
            />
            <div className="rounded-xl border border-[#ECECEE] bg-white p-5 shadow-1">
                {/* <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div>
                        <h2 className="text-lg font-semibold text-[#111315]">Insight Overview</h2>
                        <p className="text-xs text-[#62676E]">DML impact summary for the selected continuous KPI</p>
                    </div>
                    <span className="w-fit rounded-full bg-[#F2F3F4] px-3 py-1 text-[11px] font-semibold text-[#50555C]">{selectedGroup ?? 'DML'}</span>
                </div>

                <div className="mb-4 grid gap-3 md:grid-cols-4">
                    <article className="rounded-xl bg-[#FFE600] px-4 py-3 shadow-sm">
                        <span className="block text-[12px] font-semibold text-[#111315]">Estimated Uplift</span>
                        <strong className="mt-2 block text-[26px] font-bold leading-none text-[#111315]">{formatPercent(liftPct, 1)}</strong>
                        <p className="mt-2 text-[10px] text-[#4E535B]">Estimated percentage improvement versus no intervention.</p>
                    </article>

                    <article className="rounded-xl bg-[#FFE600] px-4 py-3 shadow-sm">
                        <span className="block text-[12px] font-semibold text-[#111315]">Total KPI Impact</span>
                        <strong className="mt-2 block text-[26px] font-bold leading-none text-[#111315]">{formatValueWithUnit(formatCompactSigned(totalImpact), unit)}</strong>
                        <p className="mt-2 text-[10px] text-[#4E535B]">Incremental volume across treated rows in the measurement period.</p>
                    </article>

                    <article className="rounded-xl bg-[#EFF1F3] px-4 py-3 shadow-sm">
                        <span className="block text-[12px] font-semibold text-[#111315]">Wald p-value</span>
                        <strong className="mt-2 block text-[26px] font-bold leading-none text-[#111315]">{formatPlain(waldPValue, 3)}</strong>
                        <p className="mt-2 text-[10px] text-[#4E535B]">Below 0.05 means the measured effect is unlikely to be random variation.</p>
                    </article>

                    <article className="rounded-xl bg-[#EFF1F3] px-4 py-3 shadow-sm">
                        <span className="block text-[12px] font-semibold text-[#111315]">Placebo p-value</span>
                        <strong className="mt-2 block text-[26px] font-bold leading-none text-[#111315]">{formatPlain(placeboPValue, 3)}</strong>
                        <p className="mt-2 text-[10px] text-[#4E535B]">Below 0.05 means similar effects were uncommon in false intervention tests.</p>
                    </article>
                </div> */}

                <div className="grid gap-4 xl:grid-cols-2">
                    <div className="rounded-xl border border-[#E7E8EA] bg-white p-4 shadow-sm">
                        <h3 className="text-[15px] font-semibold text-[#111315]">{t('Test vs Counterfactual')}</h3>
                        <p className="text-[10px] text-[#62676E]">{t('After-intervention average KPI')}</p>

                        <svg className="mt-3 h-[290px] w-full" viewBox="0 0 620 355" role="img" aria-label={t('Average KPI after intervention for Test and Counterfactual')} data-testid="dml-after-bars-chart">
                            <style>{`.dml-axis{stroke:#9da3ab;stroke-width:1.2}.dml-grid{stroke:#e1e4e8;stroke-width:1;stroke-dasharray:4 4}.dml-lab{font:11px Inter,Arial;fill:#737b86}.dml-value{font:bold 12px Inter,Arial;fill:#31363d}.dml-title{font:bold 12px Inter,Arial;fill:#15181c}`}</style>
                            <text className="dml-title" x="16" y="24">
                                {t('Average KPI after intervention')}
                            </text>
                            <text className="dml-lab" x="16" y="43">
                                {t('Observed Test performance compared with the DML estimate without intervention')}
                            </text>
                            <line className="dml-axis" x1="88" y1="286" x2="574" y2="286" />
                            <line className="dml-grid" x1="88" y1="226" x2="574" y2="226" />
                            <line className="dml-grid" x1="88" y1="166" x2="574" y2="166" />
                            <line className="dml-grid" x1="88" y1="106" x2="574" y2="106" />
                            <line className="dml-grid" x1="88" y1="46" x2="574" y2="46" />
                            <text className="dml-lab" x="61" y="290">
                                0
                            </text>
                            <text className="dml-lab" x="42" y="230">
                                0.5K
                            </text>
                            <text className="dml-lab" x="42" y="170">
                                1.0K
                            </text>
                            <text className="dml-lab" x="42" y="110">
                                1.5K
                            </text>
                            <text className="dml-lab" x="42" y="50">
                                2.0K
                            </text>
                            <text className="dml-lab" transform="translate(18 210) rotate(-90)">
                                {unit ? `${t('Average {{kpi}}', { kpi: kpiLabel })} (${unit})` : t('Average {{kpi}}', { kpi: kpiLabel })}
                            </text>
                            <rect x="205" y={String(286 - observedBarHeight)} width="72" height={String(observedBarHeight)} rx="12" fill="#257fa8" />
                            <rect x="357" y={String(286 - counterfactualBarHeight)} width="72" height={String(counterfactualBarHeight)} rx="12" fill="#ffe000" />
                            <text className="dml-value" x="213" y={String(286 - observedBarHeight - 11)}>
                                {formatValueWithUnit(formatCompactSigned(observedAfter), unit)}
                            </text>
                            <text className="dml-value" x="365" y={String(286 - counterfactualBarHeight - 11)}>
                                {formatValueWithUnit(formatCompactSigned(counterfactualAfter), unit)}
                            </text>
                            <text className="dml-value" x="226" y="311">
                                {t('Test')}
                            </text>
                            <text className="dml-value" x="351" y="311">
                                {t('Counterfactual')}
                            </text>
                            <circle cx="215" cy="337" r="5" fill="#257fa8" />
                            <text className="dml-lab" x="226" y="341">
                                {t('Observed after intervention')}
                            </text>
                            <circle cx="405" cy="337" r="5" fill="#ffe000" />
                            <text className="dml-lab" x="416" y="341">
                                {t('Expected without intervention')}
                            </text>
                        </svg>

                        <div className="mt-3 rounded-[7px] bg-[#EEF5F9] px-3 py-3 text-[11px] text-[#345565]">
                            <strong>{t('How to read this:')}</strong> {t("Test is the observed average after the intervention. Counterfactual is DML's estimate of what the average would have been without the intervention.")}
                        </div>
                    </div>

                    <div className="rounded-xl border border-[#E7E8EA] bg-white p-4 shadow-sm">
                        <h3 className="text-[15px] font-semibold text-[#111315]">{t('Causal effect estimate')}</h3>
                        <p className="text-[10px] text-[#62676E]">{t('The selected estimate and its 95% confidence interval')}</p>

                        <div className="mt-3 rounded-[8px] border-l-[5px] border-[#FFE600] bg-[#FFFCE7] px-4 py-3">
                            <strong className="block text-[12px] font-semibold text-[#111315]">{effectHeading}</strong>
                            <p className="mt-1 text-[11px] text-[#4F555F]">
                                {payload.Remarks ||
                                    t('The estimated average treatment effect is {{ate}} per treated record, equivalent to a {{lift}} lift. Its selected 95% interval {{interval}}', {
                                        ate: formatValueWithUnit(formatSigned(ate, 1), unit),
                                        lift: formatPercent(liftPct, 1),
                                        interval: intervalInterpretation,
                                    })}
                            </p>
                        </div>

                        <svg className="mt-3 h-[260px] w-full" viewBox="0 0 780 220" role="img" aria-label={t('Selected average treatment effect and 95 percent confidence interval')} data-testid="dml-forest-chart">
                            <style>{`.forest-axis{stroke:#cfd3d9;stroke-width:1}.forest-grid{stroke:#e4e6e9;stroke-width:1;stroke-dasharray:4 4}.forest-lab{font:12px Inter,Arial;fill:#454b54}.forest-small{font:10px Inter,Arial;fill:#767d87}.forest-bold{font:bold 11px Inter,Arial;fill:#181b20}`}</style>
                            <line className="forest-axis" x1="210" y1="175" x2="735" y2="175" />
                            <line className="forest-grid" x1={String(toForestX(lowerBound ?? 0))} y1="38" x2={String(toForestX(lowerBound ?? 0))} y2="175" />
                            <line className="forest-grid" x1={String(toForestX(0))} y1="38" x2={String(toForestX(0))} y2="175" />
                            <line className="forest-grid" x1={String(toForestX(upperBound ?? 0))} y1="38" x2={String(toForestX(upperBound ?? 0))} y2="175" />
                            <line x1={String(toForestX(0))} y1="30" x2={String(toForestX(0))} y2="175" stroke="#222" strokeWidth="1.5" strokeDasharray="5 4" />
                            <text className="forest-small" x={String(toForestX(0) - 18)} y="20">
                                {t('No effect')}
                            </text>
                            {/* <text className="forest-lab" x="15" y="93">
                                {t('Wild-cluster bootstrap')}
                            </text>
                            <text className="forest-small" x="15" y="109">
                                {t('Automatically selected for the available clusters')}
                            </text> */}
                            <text className="forest-small" x={String(toForestX(lowerBound ?? 0) - 15)} y="70">
                                {formatValueWithUnit(formatSigned(lowerBound, 1), unit)}
                            </text>
                            <text className="forest-small" x={String(toForestX(upperBound ?? 0) - 15)} y="70">
                                {formatValueWithUnit(formatSigned(upperBound, 1), unit)}
                            </text>
                            <line x1={String(toForestX(lowerBound ?? 0))} y1="94" x2={String(toForestX(upperBound ?? 0))} y2="94" stroke="#222" strokeWidth="4" />
                            <line x1={String(toForestX(lowerBound ?? 0))} y1="86" x2={String(toForestX(lowerBound ?? 0))} y2="102" stroke="#222" strokeWidth="2" />
                            <line x1={String(toForestX(upperBound ?? 0))} y1="86" x2={String(toForestX(upperBound ?? 0))} y2="102" stroke="#222" strokeWidth="2" />
                            <circle cx={String(toForestX(ate ?? 0))} cy="94" r="8" fill="#ffe600" stroke="#111" strokeWidth="2" />
                            <text className="forest-bold" x={String(Math.min(toForestX(ate ?? 0) + 120, 650))} y="98">{`ATE ${formatValueWithUnit(formatSigned(ate, 1), unit)}`}</text>
                            <text className="forest-small" x="245" y="198">
                                {unit ? `−100 ${unit}` : '−100'}
                            </text>
                            <text className="forest-small" x="375" y="198">
                                0
                            </text>
                            <text className="forest-small" x="484" y="198">
                                +100
                            </text>
                            <text className="forest-small" x="604" y="198">
                                +200
                            </text>
                            <text className="forest-small" x="724" y="198">
                                {unit ? `+300 ${unit}` : '+300'}
                            </text>
                        </svg>

                        <div className="mt-3 rounded-[7px] bg-[#EEF5F9] px-3 py-3 text-[11px] text-[#345565]">
                            <strong>{t('How to read this:')}</strong> {t('the yellow point is the average treatment effect (ATE). The horizontal line runs from ci_lower_95 to ci_upper_95.')}
                        </div>
                    </div>
                </div>
            </div>

            <section className="rounded-xl border border-[#ECECEE] bg-white p-5 shadow-1">
                <h3 className="text-[17px] font-semibold text-[#111315]">{t('Insight Overview')}</h3>
                <p className="mb-4 text-[10px] text-[#62676E]">{t('DML impact summary for the selected continuous KPI')}</p>
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5" data-testid="dml-summary-strip">
                    {summaryCards.map((card) => (
                        <article key={card.label} className={`min-h-[105px] rounded-[11px] border ${card.highlight ? 'border-[#FFE600] bg-[#FFE600]' : 'border-[#D9DDE2] bg-[#F1F2F4]'} px-4 py-3 shadow-[0_5px_12px_rgba(22,24,29,0.06)]`}>
                            <span className="block text-[11px]">{t(card.label)}</span>
                            <strong className="mt-2 block text-[20px]">{card.value}</strong>
                            <small className="mt-2 block text-[8px] leading-[1.35] text-[#656C76]">{t(card.description)}</small>
                        </article>
                    ))}
                </div>
            </section>
        </section>
    )
}

export default DMLKpi
